import styles from "./scenarioItem.module.scss";
import arrowIcon from "../../../shared/images/arrow.svg";
import infoIcon from "../../../shared/images/info.svg";
import { ReactComponent as MenuIcon } from "../../../shared/images/menu.svg";
import { Popup } from "../../../shared/components/popup/popup";
import editIcon from "../../../shared/images/edit.svg";
import { Scenario } from "../../../types/scenarioTypes";
import {
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import runIcon from "../../../shared/images/run.svg";
import { TestCaseTemplate } from "../../../types/testCaseTemplateTypes";
import {
  getServer,
  getTemplateAndTestCaseByTestCaseId,
  getTransformTestCase
} from "../../../utils/testCaseUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { LoadTestSettingsModal } from "../../loadTestSettingsModal/loadTestSettingsModal";
import { RunLoadTestModal } from "../../runLoadTestModal/runLoadTestModal";
import { ErrorType } from "../../../types/sharedTypes";
import {
  JMeterTemplateModel,
  LoadTestSettings
} from "../../../types/loadTestTypes";
import { useRunLoadTestMutation } from "../../../api/loadTestService";
import { findProjectSettings } from "../../../store/selectors";

type ScenarioItemProps = {
  scenario: Scenario;
  onClickEdit: (scenario: Scenario) => void;
  onClick: () => void;
  arrowClass: string;
  testCaseTemplates?: Array<TestCaseTemplate>;
}

export const ScenarioItem = ({ scenario, onClickEdit, onClick, arrowClass, testCaseTemplates }: ScenarioItemProps) => {
  const { t } = useTranslation();

  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);
  const methods = useSelector((state: RootState) => state.mainSlice.methods);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const menuContainer = useRef<HTMLDivElement>(null);

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const [showLoadTestSettings, setShowLoadTestSettings] = useState<boolean>(false);
  const [showRunLoadTest, setShowRunLoadTest] = useState<boolean>(false);

  const [runLoadTest, { error: errorRunLoadTest, isLoading: runLoadTestIsLoading  }] = useRunLoadTestMutation();

  const onClickMenuIcon = (): void => {
    setShowPopup(!showPopup);
  }

  const closeMenu = (): void => {
    setShowPopup(false);
  }

  const clickEdit = (): void => {
    setShowPopup(false);
    onClickEdit(scenario);
  }

  const runLoadTestClick = (): void => {
    setShowPopup(false);
    setShowLoadTestSettings(true);
  }

  const closeLoadTestSettings = (settings?: LoadTestSettings): void => {
    if (!!settings) {
      const jMeterTemplateModels: Array<JMeterTemplateModel> = [];
      scenario.assignedRequests.forEach(assignedRequest => {
        const templateAndTestCase = getTemplateAndTestCaseByTestCaseId(testCaseTemplates || [], assignedRequest.requestId);
        if (templateAndTestCase) {
          const findMethod = methods.find(method => method.id === templateAndTestCase.template.methodId);
          const server = getServer(templateAndTestCase.template, selectedEnvironment);
          if (!!findMethod && !!server) {
            const transformTestCase = getTransformTestCase(selectedVariables, templateAndTestCase.template, templateAndTestCase.testCase);
            jMeterTemplateModels.push({
              url: `${server.url || ""}${transformTestCase.endpoint}`,
              authToken: server.authToken || "",
              testCaseName: transformTestCase.name,
              body: transformTestCase.body || "",
              method: findMethod.name,
              headers: transformTestCase.requestHeaders?.map(header => ({ name: header.name, value: header.value || "" })) || []
            });
          }
        }
      });
      runLoadTest({
        ...settings,
        jMeterTemplateModels
      });
      setShowRunLoadTest(true);
    }
    setShowLoadTestSettings(false);
  }

  const closeRunLoadTestModal = (): void => {
    setShowRunLoadTest(false);
  }

  const showRunLoadTestButton = useMemo(() => {
    return scenario.assignedRequests.some(assignedRequest => {
      const templateAndTestCase = getTemplateAndTestCaseByTestCaseId(testCaseTemplates || [], assignedRequest.requestId);
      if (templateAndTestCase) {
        return getServer(templateAndTestCase.template, selectedEnvironment);
      }
    });
  }, [scenario, testCaseTemplates]);

  return (
    <>
      <div className={styles.container}
           onMouseEnter={() => setShowMenu(true)}
           onMouseLeave={() => setShowMenu(false)}
           onClick={onClick}>
        <div className={styles.info}>
          <img src={arrowIcon} alt=""
               className={arrowClass}/>
          <div className={styles.name}>
            <span title={scenario.name}>{scenario.name}</span>
            {!!scenario.description && (
              <img title={scenario.description} className={styles.description} src={infoIcon}
                   alt=""/>
            )}
          </div>

        </div>
      </div>
      {(showMenu || showPopup) && (
        <div className={styles.actions}
             onMouseEnter={() => setShowMenu(true)}
             onMouseLeave={() => setShowMenu(false)}>
          <div className={styles.menu} ref={menuContainer}>
            <div className={styles.menuIcon}
                 onClick={onClickMenuIcon}>
              <MenuIcon/>
            </div>
            <Popup isShow={showPopup}
                   classContainer={styles.popup}
                   left={-70}
                   top={5}
                   parent={menuContainer.current}
                   onClose={closeMenu}
                   child={
                     <>
                       <div className={styles.item}
                            onClick={clickEdit}>
                         <img src={editIcon} alt=""/>
                         {t("common.edit")}
                       </div>
                       { showRunLoadTestButton && (
                         <div className={styles.item}
                              onClick={runLoadTestClick}>
                           <img src={runIcon} alt=""/>
                           {t("loadTestSettings.run")}
                         </div>
                       )}

                     </>
                   }
            />
          </div>
        </div>
      )}
      {showLoadTestSettings && (<LoadTestSettingsModal onClose={closeLoadTestSettings} />)}
      <RunLoadTestModal isShow={showRunLoadTest} isLoading={runLoadTestIsLoading} errorData={errorRunLoadTest as ErrorType} closeModal={closeRunLoadTestModal}/>
    </>

  );
}